import * as React from "react";
import { useLocation } from "react-router-dom";
import useSWR from "swr";
import { isNoticeShownOn } from "../domain/siteNotice";
import { fetcher } from "../hooks";
import Carousel from "../theme/Carousel";
import css from "./SiteNotice.module.css";
import { ChevronLeftIcon, ChevronRightIcon } from "../themeV2";

function useSiteNotices() {
  const { data, error } = useSWR("/api/siteNotice", fetcher);

  const { notices, config } = React.useMemo(() => {
    if (!data || !data?.notices || !data?.config) {
      return {
        notices: [],
        config: {
          defaultInterval: 6 * 1000,
          longInterval: 60 * 1000,
          backgroundColor: "#e8e8e8",
          color: "#2b2b2b",
        },
      };
    }

    return data;
  }, [data]);

  return { notices, config, isLoading: !error && !data, isError: error };
}

export default function SiteNotice() {
  const location = useLocation();
  const { notices, config } = useSiteNotices();

  const noticesToDisplay = React.useMemo(
    () => notices.filter(isNoticeShownOn(location.pathname.toLowerCase())),
    [notices, location.pathname],
  );

  const [activeStep, setActiveStep] = React.useState(0);
  const [interval, setInterval] = React.useState(8 * 1000);

  const maxSteps = noticesToDisplay.length;

  React.useEffect(() => {
    if (noticesToDisplay.length) {
      setActiveStep(0);
    }
  }, [location.pathname, noticesToDisplay.length]);

  // Keep activeStep in bounds if notices change
  React.useEffect(() => {
    if (maxSteps > 0 && activeStep >= maxSteps) {
      setActiveStep(0);
    }
  }, [activeStep, maxSteps]);

  if (!noticesToDisplay.length) {
    return null;
  }

  const handlePrev = () => {
    setActiveStep((prev) => (prev - 1 + maxSteps) % maxSteps);
  };

  const handleNext = () => {
    setActiveStep((prev) => (prev + 1) % maxSteps);
  };

  return (
    <div
      style={{ color: config.color, backgroundColor: config.backgroundColor }}
      className={css.notice}
      onPointerEnter={() => setInterval(config.longInterval)}
      onPointerLeave={() => setInterval(config.defaultInterval)}
    >
      <div
        className={css.main}
        style={{ color: config.color, backgroundColor: config.backgroundColor }}
      >
        {maxSteps > 1 && (
          <button className={css.button} title="Back" onClick={handlePrev}>
            <ChevronLeftIcon className={css.icon} />
          </button>
        )}

        <Carousel
          className={css.swipe}
          interval={interval}
          index={activeStep}
          onChangeIndex={setActiveStep}
          enableMouseEvents
          autoPlay
        >
          {noticesToDisplay.map((notice, index) => (
            <div
              key={notice.id || index}
              style={{
                color: config.color,
                backgroundColor: config.backgroundColor,
              }}
              className={css.content}
              dangerouslySetInnerHTML={{ __html: notice?.content }}
            />
          ))}
        </Carousel>

        {maxSteps > 1 && (
          <button className={css.button} title="Next" onClick={handleNext}>
            <ChevronRightIcon className={css.icon} />
          </button>
        )}
      </div>
    </div>
  );
}
